import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type PlanTier = 'free' | 'core' | 'pro';

export interface SubscriptionData {
  subscribed: boolean;
  plan_tier: PlanTier;
  is_admin: boolean;
  status: string;
  subscription_end: string | null;
  trial_ends_at: string | null;
  is_student: boolean;
  cancel_at_period_end: boolean;
}

export interface UsageData {
  analyses_count: number;
  essays_count: number;
  billing_period: string;
}

export interface FeatureAccess {
  allowed: boolean;
  tier: PlanTier;
  is_admin: boolean;
  limit: number;
  used: number;
  remaining: number;
  reason?: string;
  clean_exports: boolean;
}

interface SubscriptionContextType {
  subscription: SubscriptionData | null;
  usage: UsageData | null;
  loading: boolean;
  error: string | null;
  tier: PlanTier;
  isAdmin: boolean;
  isStudent: boolean;
  refreshSubscription: () => Promise<void>;
  checkFeatureAccess: (feature: string, analysisId?: string) => Promise<FeatureAccess>;
  incrementUsage: (feature: 'analysis' | 'essay') => Promise<void>;
  openCheckout: (plan: 'core' | 'pro', billing: 'monthly' | 'annual', isStudent?: boolean) => Promise<void>;
  openCustomerPortal: () => Promise<void>;
}

const defaultSubscription: SubscriptionData = {
  subscribed: false,
  plan_tier: 'free',
  is_admin: false,
  status: 'active',
  subscription_end: null,
  trial_ends_at: null,
  is_student: false,
  cancel_at_period_end: false,
};

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshSubscription = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        setSubscription(defaultSubscription);
        setUsage(null);
        setLoading(false);
        return;
      }

      const { data, error: fnError } = await supabase.functions.invoke('check-subscription');
      
      if (fnError) {
        console.error('Error checking subscription:', fnError);
        setError(fnError.message);
        setSubscription(defaultSubscription);
      } else {
        // Log subscription status for debugging
        console.log('[Subscription] Status:', {
          tier: data.plan_tier,
          isAdmin: data.is_admin,
          email: session.user.email,
        });
        setSubscription(data as SubscriptionData);
        setError(null);
      }

      // Get usage data
      const now = new Date();
      const billingPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      
      const { data: usageData } = await supabase
        .from('usage_tracking')
        .select('*')
        .eq('billing_period', billingPeriod)
        .single();

      if (usageData) {
        setUsage({
          analyses_count: usageData.analyses_count,
          essays_count: usageData.essays_count,
          billing_period: usageData.billing_period,
        });
      }
    } catch (err) {
      console.error('Error in refreshSubscription:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setSubscription(defaultSubscription);
    } finally {
      setLoading(false);
    }
  }, []);

  const checkFeatureAccess = useCallback(async (feature: string, analysisId?: string): Promise<FeatureAccess> => {
    try {
      const { data, error } = await supabase.functions.invoke('check-feature-access', {
        body: { feature, analysisId },
      });

      if (error) throw error;
      return data as FeatureAccess;
    } catch (err) {
      console.error('Error checking feature access:', err);
      // Default to allowing access on error to avoid blocking users
      return {
        allowed: true,
        tier: subscription?.plan_tier || 'free',
        is_admin: subscription?.is_admin || false,
        limit: 0,
        used: 0,
        remaining: 0,
        clean_exports: subscription?.plan_tier !== 'free',
      };
    }
  }, [subscription]);

  const incrementUsage = useCallback(async (feature: 'analysis' | 'essay') => {
    try {
      await supabase.functions.invoke('increment-usage', {
        body: { feature },
      });
      // Refresh to get updated usage
      await refreshSubscription();
    } catch (err) {
      console.error('Error incrementing usage:', err);
    }
  }, [refreshSubscription]);

  const openCheckout = useCallback(async (plan: 'core' | 'pro', billing: 'monthly' | 'annual', requestStudent?: boolean) => {
    try {
      console.log('[Checkout] Invoking create-checkout:', { plan, billing, requestStudent });
      
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { plan, billing, requestStudent },
      });

      console.log('[Checkout] Response:', { data, error });

      if (error) {
        console.error('[Checkout] Function error:', error);
        throw error;
      }

      if (data?.url) {
        console.log('[Checkout] Redirecting to Stripe:', data.url);
        window.location.href = data.url;
      } else {
        console.error('[Checkout] No URL in response:', data);
        throw new Error('No checkout URL returned from server');
      }
    } catch (err) {
      console.error('[Checkout] Error:', err);
      throw err;
    }
  }, []);

  const openCustomerPortal = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');

      if (error) throw error;

      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Error opening customer portal:', err);
      throw err;
    }
  }, []);

  // Initial load and auth state changes
  useEffect(() => {
    refreshSubscription();

    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(() => {
      refreshSubscription();
    });

    return () => {
      authSubscription.unsubscribe();
    };
  }, [refreshSubscription]);

  // Periodic refresh every minute
  useEffect(() => {
    const interval = setInterval(refreshSubscription, 300000);
    return () => clearInterval(interval);
  }, [refreshSubscription]);

  const value: SubscriptionContextType = {
    subscription,
    usage,
    loading,
    error,
    tier: subscription?.plan_tier || 'free',
    isAdmin: subscription?.is_admin || false,
    isStudent: subscription?.is_student || false,
    refreshSubscription,
    checkFeatureAccess,
    incrementUsage,
    openCheckout,
    openCustomerPortal,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}
